import { Intent } from '@blueprintjs/core';
import * as go from 'gojs';
import * as React from 'react';
import { alphaSort } from 'vet-bones/utils';
import { SelectItem } from 'vet-bones/utils/select';

import { PropertyTypeSelector } from 'src/components/PropertyTypeSelector';
import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import { SelectableLinks } from 'src/components/SelectableLinks';
import {
  DEFAULT_PROJECT_PROPERTY_TYPE,
  ProjectPropertyType,
} from 'src/constants/project';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
  PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectLinkTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { ProjectDiagramEditableIri } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableIri';
import { ProjectDiagramEditableTextArea } from 'src/containers/ProjectDiagram/components/ProjectDiagramEditableTextArea';
import { ProjectDiagramRelationshipDrawerTable } from 'src/containers/ProjectDiagram/components/relationshipDrawer/ProjectDiagramRelationshipDrawerTable';
import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useCreateModelConceptClassFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptClassFromQuery';
import { useGenerateNewIri } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useGenerateNewIri';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ProjectDiagramCreateEntityDrawer } from 'src/containers/ProjectDiagram/ProjectDiagramCreateEntityDrawer';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { setDescriptionForIri } from 'src/containers/ProjectDiagram/utils/descriptions';
import { generateModelConceptIri } from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import { setLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { addDomainOrRangeToRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addDomainOrRangeToRelationship';
import { addUnconnectedModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addUnconnectedModelConceptRelationship';
import {
  addProjectModelIdsToIri,
  getProjectModelIdsForIri,
} from 'src/containers/ProjectDiagram/utils/projectModelIds';
import { setPropertyType } from 'src/containers/ProjectDiagram/utils/propertyType/setPropertyType';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import { projectSelector } from 'src/selectors/project';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectModelItemsSelector } from 'src/selectors/projectModelItems';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { isCustomPropertyTypeSelector } from 'src/selectors/propertyType';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { getProjectTargetModel } from 'src/utils/getProjectTargetModel';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramCreateRelationshipDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const generateNewIri = useGenerateNewIri();
  const { projectId } = useRouterParams();

  const project = useReduxSelector(projectSelector(projectId));

  const { isOpen, sourceClassIri, targetClassIri } = useReduxSelector(
    (state) => state.ui.createRelationshipDrawer
  );
  const didIsOpenChange = useDidValueChange(isOpen);

  const allModelItems = useReduxSelector(projectModelItemsSelector(projectId));

  const allClassItems: SelectItem[] = useReduxSelector(
    projectClassItemsSelector(projectId)
  );

  const [errorText, setErrorText] = React.useState(
    copy.components.projectDiagram.editableIri.validationLabelRequired
  );
  const [iri, setIri] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');

  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );
  const [models, setModels] = React.useState<SelectItem[]>(
    allModelItems.filter((item) => item.value === targetModelId)
  );
  const [didEditLabel, setDidEditLabel] = React.useState(false);

  const isCustomPropertyType = useReduxSelector(
    isCustomPropertyTypeSelector(projectId)
  );
  const [localPropertyType, setLocalPropertyType] =
    React.useState<ProjectPropertyType>(DEFAULT_PROJECT_PROPERTY_TYPE);

  const [domains, setDomains] = React.useState<SelectItem[]>([]);
  const [ranges, setRanges] = React.useState<SelectItem[]>([]);

  const availableModels = React.useMemo(() => {
    const modelIds = new Set(models.map((item) => item.value));

    return allModelItems.filter((item) => !modelIds.has(item.value));
  }, [allModelItems, models]);

  React.useEffect(() => {
    if (didIsOpenChange && isOpen) {
      const sourceClassModels =
        sourceClassIri && diagram
          ? getProjectModelIdsForIri(sourceClassIri, diagram.model)
          : null;
      const targetClassModels =
        targetClassIri && diagram
          ? getProjectModelIdsForIri(targetClassIri, diagram.model)
          : null;
      const classModels = allModelItems.filter(
        (item) =>
          sourceClassModels?.includes(item.value) ||
          targetClassModels?.includes(item.value)
      );

      setModels(
        classModels.length
          ? classModels
          : allModelItems.filter((item) => item.value === targetModelId)
      );
      setDomains(allClassItems.filter((item) => item.value === sourceClassIri));
      setRanges(allClassItems.filter((item) => item.value === targetClassIri));
    }
  }, [
    allClassItems,
    allModelItems,
    diagram,
    didIsOpenChange,
    isOpen,
    sourceClassIri,
    targetClassIri,
    targetModelId,
  ]);

  const domainIris = new Set(domains.map((item) => item.value));
  const availableDomains = allClassItems.filter(
    (item) => !domainIris.has(item.value)
  );

  const rangeIris = new Set(ranges.map((item) => item.value));
  const availableRanges = allClassItems.filter(
    (item) => !rangeIris.has(item.value)
  );

  const createNewClassFromQuery = useCreateModelConceptClassFromQuery();

  const handleUpdateIri = (iri: string, invalid: string) => {
    setIri(iri);
    setErrorText(invalid);
  };
  const handleUpdateLabel = (label: string, invalid: string) => {
    setLabel(label);
    setDidEditLabel(true);
    setErrorText(invalid);
    if (!label) {
      setIri('');
      return;
    }

    if (!project) {
      return;
    }

    const initialLabelIri = generateModelConceptIri(
      getProjectTargetModel(project),
      label.trim()
    );
    const labelIri = generateNewIri(initialLabelIri);
    setIri(labelIri);
  };

  const handleAddModel = (selected: SelectItem | null) => {
    if (!selected) {
      return;
    }
    setModels([...models, selected].sort(alphaSort((item) => item.text)));
  };
  const handleRemoveModel = (selected: SelectItem) => {
    setModels(models.filter((item) => item.value !== selected.value));
  };

  const handleAddDomain = (selected: SelectItem | null) => {
    if (!selected || (selected.isNewlyCreated && !selected.text)) {
      return;
    }
    setDomains([...domains, selected].sort(alphaSort((item) => item.text)));
  };
  const handleRemoveDomain = (selected: SelectItem) => {
    setDomains(domains.filter((item) => item.value !== selected.value));
  };

  const handleAddRange = (selected: SelectItem | null) => {
    if (!selected || (selected.isNewlyCreated && !selected.text)) {
      return;
    }
    setRanges([...ranges, selected].sort(alphaSort((item) => item.text)));
  };
  const handleRemoveRange = (selected: SelectItem) => {
    setRanges(ranges.filter((item) => item.value !== selected.value));
  };

  const handleCancel = () => {
    setErrorText(
      copy.components.projectDiagram.editableIri.validationLabelRequired
    );
    setIri('');
    setLabel('');
    setDescription('');
    setModels([]);
    setDidEditLabel(false);
    setLocalPropertyType(DEFAULT_PROJECT_PROPERTY_TYPE);
    setDomains([]);
    setRanges([]);
    dispatch(
      uiSlice.actions.toggledCreateRelationshipDrawer({
        isOpen: false,
      })
    );
  };

  const handleSubmit = () => {
    if (errorText || !diagram) {
      return;
    }

    commitDiagramModel(
      diagram,
      (model, diagram) => {
        const projectModelIds = models.map(({ value }) => value);

        if (domains.length || ranges.length) {
          domains.forEach((domain) => {
            if (domain.isNewlyCreated) {
              addModelConceptClass({
                iri: domain.value,
                label: domain.text,
                projectModelIds,
              })(model);
            }
            addDomainOrRangeToRelationship({
              direction: RelationshipDirection.OUTGOING,
              relationshipIri: iri,
              classIri: domain.value,
              useAllValidLinks: true,
            })(model, diagram);
          });
          ranges.forEach((range) => {
            if (range.isNewlyCreated) {
              addModelConceptClass({
                iri: range.value,
                label: range.text,
                projectModelIds,
              })(model);
            }
            addDomainOrRangeToRelationship({
              direction: RelationshipDirection.INCOMING,
              relationshipIri: iri,
              classIri: range.value,
              useAllValidLinks: true,
            })(model, diagram);
          });
        } else {
          addUnconnectedModelConceptRelationship({
            value: iri,
          })(model, diagram);
        }
        setLabelForIri(iri, label)(model);
        setDescriptionForIri(iri, description)(model);
        setPropertyType(
          iri,
          isCustomPropertyType ? localPropertyType : undefined
        )(model);
        addProjectModelIdsToIri(iri, projectModelIds)(model);
      },
      { layout: DiagramModelCommitLayout.PARTIAL }
    );

    // Selecting the new link + moving the viewport are canvas-only.
    if (!diagram.headless) {
      const linkToSelect = diagram
        .findLinksByExample({
          value: iri,
          category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP,
        })
        .first();
      if (linkToSelect) {
        // select link (shows inspector), and move viewport so that it is visible
        commitDiagramWhileSkippingUndoManager(
          diagram,
          // TODO: move viewport for relationships once createdrelationship
          // position is not based on the center of the viewport
          (_, diagram) =>
            setDiagramSelection(new go.Set([linkToSelect as go.Link]))(diagram),
          DiagramTransaction.SET_DIAGRAM_SELECTION
        );
      }
    }

    handleCancel();
  };

  return (
    <ProjectDiagramCreateEntityDrawer
      disabled={Boolean(errorText)}
      errorIntent={didEditLabel ? Intent.DANGER : Intent.NONE}
      errorText={errorText}
      isOpen={isOpen}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      title={copy.components.projectDiagram.relationshipDrawer.createTitle}
    >
      <ProjectDiagramEditableIri
        errorIntent={didEditLabel ? Intent.DANGER : Intent.NONE}
        header={copy.components.projectDiagram.relationshipDrawer.name}
        iri={iri}
        label={label}
        onSetIri={handleUpdateIri}
        onSetLabel={handleUpdateLabel}
        placeholder={
          label === ''
            ? copy.components.projectDiagram.modelConceptRelationship.new
            : ''
        }
        setValueOnChange
        testId={TEST_IDS.projectDiagram.entityDrawer.name}
      />
      <ProjectDiagramEditableTextArea
        header={copy.components.projectDiagram.entityDrawer.description}
        onSetValue={setDescription}
        value={description}
      />

      {allModelItems.length > 1 ? (
        <SelectableLinks
          allowEmpty={false}
          buttonText=""
          filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
          header={copy.components.projectDiagram.entityDrawer.models}
          items={availableModels}
          itemTestId={(value, idx) =>
            getProjectDiagramEntityDrawerSelectLinkTestId(value, 'models', idx)
          }
          onRemove={handleRemoveModel}
          onSelect={handleAddModel}
          removeDisabledTooltip={
            copy.components.projectDiagram.entityDrawer.modelsRemoveDisabled
          }
          selected={models}
          selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          selectorTestId={getProjectDiagramEntityDrawerSelectorTestId('models')}
        />
      ) : null}

      {isCustomPropertyType ? (
        <PropertyTypeSelector
          propertyType={localPropertyType}
          setPropertyType={setLocalPropertyType}
        />
      ) : null}

      <div className="sd-drawer-project-diagram-entity-columns">
        <SelectableLinks
          buttonText=""
          creatable
          createNewItemFromQuery={createNewClassFromQuery}
          createNewItemRenderer={createNewSelectItemRenderer}
          createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
            ''
          )}
          filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
          header={copy.components.projectDiagram.relationshipDrawer.domain}
          items={availableDomains}
          itemTestId={(value, idx) =>
            getProjectDiagramEntityDrawerSelectLinkTestId(
              value,
              PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE,
              idx
            )
          }
          onRemove={handleRemoveDomain}
          onSelect={handleAddDomain}
          selected={domains}
          selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
            PROJECT_DIAGRAM_ENTITY_DRAWER_DOMAIN_TYPE
          )}
        />
        <SelectableLinks
          buttonText=""
          creatable
          createNewItemFromQuery={createNewClassFromQuery}
          createNewItemRenderer={createNewSelectItemRenderer}
          createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
            ''
          )}
          filterTestId={TEST_IDS.projectDiagram.entityDrawer.selectorFilter}
          header={copy.components.projectDiagram.relationshipDrawer.range}
          items={availableRanges}
          itemTestId={(value, idx) =>
            getProjectDiagramEntityDrawerSelectLinkTestId(
              value,
              PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE,
              idx
            )
          }
          onRemove={handleRemoveRange}
          onSelect={handleAddRange}
          selected={ranges}
          selectorItemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          selectorTestId={getProjectDiagramEntityDrawerSelectorTestId(
            PROJECT_DIAGRAM_ENTITY_DRAWER_RANGE_TYPE
          )}
        />
      </div>

      <ProjectDiagramRelationshipDrawerTable
        domains={domains}
        ranges={ranges}
      />
    </ProjectDiagramCreateEntityDrawer>
  );
};
